import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";

import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils.js";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english.js";
import { getLanguage } from "../../config/language/language.js";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying libraries with filters, pagination, sorting etc.
 */
export const queryLibraries = (props, options = {}) => {
    const path = Constants.API_LIBRARIES + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying libraries with filters, pagination, sorting etc.
 * NB! This is only meant for library selection pop-up.
 */
export const querySelectLibraries = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_LIBRARIES + params;

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Function for querying an existing library.
 */
export const queryLibrary = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID, { library_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for creating a new library.
 */
export const createLibrary = (props, options = {}) => {
    const path = Constants.API_LIBRARIES;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a library.
 */
export const updateLibrary = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID, { library_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for deleting libraries.
 */
export const deleteLibraries = (props, options = {}) => {
    let path = Constants.API_LIBRARIES;
    if (options?.deleteResources) path += "?resources=true";

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.libraries;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        if (options?.deleteResources) {
            // Delete all resources related to resources from states.
            const resources = props.backend.resources;
            const resourceIds = resources.data.filter((r) => options.ids.includes(r.library_id)).map((r) => r.id);

            setDeleteArrayState(resources.setData, resourceIds);
            resources.setRowCount(Math.max(0, resources.rowCount - resourceIds.length));
            resources.setSelected([]);

            // Delete all transcripts related to resources from states.
            const transcripts = props.backend.transcripts;
            const transcriptIds = transcripts.data.filter((t) => options.ids.includes(t.resource_id)).map((t) => t.id);

            setDeleteArrayState(transcripts.setData, transcriptIds);
            transcripts.setRowCount(Math.max(0, transcripts.rowCount - transcriptIds.length));
            transcripts.setSelected([]);

            // Delete all quotes related to transcripts from states.
            const quotes = props.backend.quotes;
            const quoteIds = quotes.data.filter((q) => transcriptIds.includes(q.transcript_id)).map((q) => q.id);

            setDeleteArrayState(quotes.setData, quoteIds);
            quotes.setRowCount(Math.max(0, quotes.rowCount - quoteIds.length));
            quotes.setSelected([]);
        }

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Function for approving a library.
 */
export const approveLibrary = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID_APPROVE, { library_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

export const approveLibraries = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_APPROVE, options.body, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for approving a library.
 */
export const disapproveLibrary = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID_DISAPPROVE, { library_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

export const disapproveLibraries = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_DISAPPROVE, options.body, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a library.
 */
export const retryLibrary = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID_RETRY, { library_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

export const retryLibraries = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_RETRY, options.body, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a library.
 */
export const retryLibraryMetadata = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID_RETRY_METADATA, { library_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.libraries;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for manually starting library queue process.
 */
export const startProcessLibrary = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_START_PROCESS, undefined, (response) => {
        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for manually stopping library queue process.
 */
export const stopProcessLibrary = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_STOP_PROCESS, undefined, (response) => {
        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for sending a webhook to ARO with all resources and playlists.
 */
export const sendWebhook = (props, options = {}) => {
    standardPutQuery(props, Constants.API_LIBRARIES_SEND_WEBHOOK, { library_ids: options.libraryIds || [] }, () => {
        props.showSnackbar({ message: getLanguage().SNACKBAR_LIBRARY_WEBHOOK_SENT });
    });
};

/**
 * Function for manually rereading one YouTube channel.
 */
export const recheckLibraryCatalog = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_LIBRARIES_ID_RECHECK, { library_id: options.id });

    standardPutQuery(props, path, undefined, () => {
        props.showSnackbar({ message: getLanguage().SNACKBAR_LIBRARY_RECHECK_STARTED });
    });
};
